/**
 * StylePreview — Audio preview for style selection
 * Plays short audio samples so users know what each style sounds like
 */
import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

// Style preview snippets served from frontend/public/style-previews.
export const STYLE_PREVIEWS = {
  trap: {
    description: '808 profundo • Hi-hats rápidos • Dark vibes',
    characteristics: ['140-150 BPM', 'Tonalidad menor', 'Snare contundente'],
    previewUrl: '/style-previews/trap.wav',
  },
  reggaeton: {
    description: 'Dembow clásico • Flow latino • Perreo',
    characteristics: ['90-100 BPM', 'Patrón dembow', 'Muy bailable'],
    previewUrl: '/style-previews/reggaeton.wav',
  },
  electronic: {
    description: 'Synths épicos • Build-up • Drop potente',
    characteristics: ['125-130 BPM', 'Side-chain', 'Energía alta'],
    previewUrl: '/style-previews/electronic.wav',
  },
  pop: {
    description: 'Gancho pegadizo • Producción limpia • Radio-ready',
    characteristics: ['100-120 BPM', 'Tonalidad mayor', 'Memorable'],
    previewUrl: '/style-previews/pop.wav',
  },
  rnb: {
    description: 'Soul moderno • Groovy • Emotivo',
    characteristics: ['70-90 BPM', 'Acordes ricos', 'Smooth'],
    previewUrl: '/style-previews/rnb.wav',
  },
  surprise: {
    description: 'Experimental • Único • Inesperado',
    characteristics: ['Variable', 'Fusion', 'Creativo'],
    previewUrl: '/style-previews/surprise.wav',
  },
};

export function StylePreviewCard({ styleId, isSelected, onSelect, showDetails = false }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const audioRef = useRef(null);
  
  const preview = STYLE_PREVIEWS[styleId] || STYLE_PREVIEWS.pop;
  
  useEffect(() => {
    // Stop playing when deselected
    if (!isSelected && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isSelected, isPlaying]);
  
  const togglePreview = (e) => {
    e.stopPropagation();
    if (!preview.previewUrl) {
      // No preview available - could show a message
      return;
    }
    
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Preview audio element */}
      {preview.previewUrl && (
        <audio
          ref={audioRef}
          src={preview.previewUrl}
          onEnded={() => setIsPlaying(false)}
          loop={false}
        />
      )}
      
      {/* Hover details overlay */}
      {(isHovered || showDetails) && (
        <div className="absolute inset-0 bg-black/80 rounded-xl sm:rounded-2xl p-2 flex flex-col justify-between z-10 pointer-events-none">
          <p className="text-[8px] sm:text-[9px] text-white/90 leading-tight">
            {preview.description}
          </p>
          <div className="flex flex-wrap gap-1 mt-1">
            {preview.characteristics.map((char, idx) => (
              <span key={idx} className="text-[7px] px-1.5 py-0.5 bg-white/10 rounded text-white/70">
                {char}
              </span>
            ))}
          </div>
          
          {/* Preview play button */}
          {preview.previewUrl && (
            <button
              onClick={togglePreview}
              className="absolute bottom-2 right-2 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center pointer-events-auto hover:bg-white/30 transition-colors"
            >
              {isPlaying ? <Pause size={10} /> : <Play size={10} className="ml-0.5" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Waveform visualization for style (generated, not real audio)
export function StyleWaveform({ styleId, color, width = 100, height = 24 }) {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    
    // Generate style-specific waveform pattern
    const patterns = {
      trap: () => generateTrapPattern(ctx, width, height, color),
      reggaeton: () => generateDembowPattern(ctx, width, height, color),
      electronic: () => generateElectroPattern(ctx, width, height, color),
      pop: () => generatePopPattern(ctx, width, height, color),
      rnb: () => generateRnbPattern(ctx, width, height, color),
      surprise: () => generateRandomPattern(ctx, width, height, color),
    };
    
    ctx.clearRect(0, 0, width, height);
    (patterns[styleId] || patterns.pop)();
  }, [styleId, color, width, height]);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{ width, height }}
      className="opacity-60"
    />
  );
}

// Pattern generators
function generateTrapPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    // Trap: strong kicks on 1, snares on 3, hi-hats rapid
    const isKick = i % 4 === 0;
    const isSnare = i % 4 === 2;
    const amp = isKick ? 0.9 : isSnare ? 0.7 : 0.2 + Math.random() * 0.3;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

function generateDembowPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    // Dembow: characteristic boom-ch-boom-chick pattern
    const isDembow = i % 4 === 1 || i % 4 === 3;
    const isKick = i % 4 === 0;
    const amp = isKick ? 0.85 : isDembow ? 0.6 : 0.3;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

function generateElectroPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    // Electro: four-on-floor with build-ups
    const buildUp = i > 10;
    const amp = buildUp ? 0.5 + (i - 10) * 0.1 : i % 2 === 0 ? 0.8 : 0.4;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

function generatePopPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    // Pop: consistent, catchy rhythm
    const amp = 0.5 + Math.sin(i / 3) * 0.3;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

function generateRnbPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    // R&B: smooth, groovy
    const amp = 0.4 + Math.sin(i / 2) * 0.2 + Math.random() * 0.1;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

function generateRandomPattern(ctx, w, h, color) {
  const bars = 16;
  const barWidth = w / bars - 1;
  ctx.fillStyle = color;
  
  for (let i = 0; i < bars; i++) {
    const amp = 0.2 + Math.random() * 0.7;
    const barHeight = h * amp;
    ctx.fillRect(i * (barWidth + 1), (h - barHeight) / 2, barWidth, barHeight);
  }
}

export default StylePreviewCard;
